import React, { useCallback, useMemo, useRef, useState } from 'react';
import { useContext, useContextSelector, } from 'use-context-selector';
import { useCurrent, useForceUpdate, useIsomorphicLayoutEffect as useLayoutEffect, } from '../../utils';
export function DescendantsProvider(props) {
    const { children, context: DescandantsContext, defaultActiveIndex = -1, } = props;
    const [descendants, setDescendants] = useState([]);
    const [activeIndex, setActiveIndex] = useState(defaultActiveIndex);
    const [selected, setSelected] = useState([]);
    const activeIndexRef = useCurrent(activeIndex);
    const getActiveIndex = useCallback(() => activeIndexRef.current, [activeIndexRef]);
    const selectedRef = useCurrent(selected);
    const getSelected = useCallback(() => selectedRef.current, [selectedRef]);
    const descendantsRef = useCurrent(descendants);
    const getDescendants = useCallback(() => descendantsRef.current, [descendantsRef]);
    const visibleDescendantsRef = useRef(descendants);
    const getVisibleDescendants = useCallback(() => visibleDescendantsRef.current, [visibleDescendantsRef]);
    const registerDescendant = useCallback(({ element, index: explicitIndex, ...rest }) => {
        if (!element)
            return () => undefined;
        setDescendants((descendants) => {
            var _a, _b, _c, _d;
            if (explicitIndex != null && explicitIndex !== -1) {
                return insertAt(descendants, { ...rest, element, index: explicitIndex }, ((_a = descendants[0]) === null || _a === void 0 ? void 0 : _a.index) > explicitIndex
                    ? 0
                    : ((_b = descendants[descendants.length - 1]) === null || _b === void 0 ? void 0 : _b.index) < explicitIndex
                        ? undefined
                        : explicitIndex);
            }
            if (descendants.length === 0) {
                return [{ index: 0, element, ...rest }];
            }
            const index = findIndexByElement(descendants, element);
            let newDescendants;
            if (index === -1) {
                newDescendants = [
                    ...descendants,
                    { ...rest, element, index: descendants.length },
                ];
            }
            else {
                newDescendants = insertAt(descendants, { ...rest, element, index }, ((_c = descendants[0]) === null || _c === void 0 ? void 0 : _c.index) > index
                    ? 0
                    : ((_d = descendants[descendants.length - 1]) === null || _d === void 0 ? void 0 : _d.index) < index
                        ? undefined
                        : index);
            }
            return newDescendants;
        });
        return () => {
            if (!element)
                return;
            setDescendants((descendants) => descendants.filter((descendant) => element !== descendant.element));
        };
    }, []);
    const setSelectedIndex = useCallback((index, allowMultiple = false) => {
        const descendant = descendants[index];
        if (descendant) {
            setSelected((descendants) => {
                if (allowMultiple) {
                    return [...descendants, descendant];
                }
                return [descendant];
            });
        }
    }, [descendants, setSelected]);
    useLayoutEffect(() => {
        visibleDescendantsRef.current = descendants
            .filter((descendant) => {
            var _a;
            if ('hidden' in descendant && descendant.hidden === true) {
                return false;
            }
            if (((_a = descendant.element) === null || _a === void 0 ? void 0 : _a.getAttribute('disabled')) != null) {
                return false;
            }
            return true;
        })
            .sort((a, b) => a.index - b.index);
    }, [descendants]);
    return (React.createElement(DescandantsContext.Provider, { value: useMemo(() => ({
            descendants,
            getDescendants,
            getVisibleDescendants,
            registerDescendant,
            activeIndex,
            setActiveIndex,
            getActiveIndex,
            selected,
            getSelected,
            setSelected,
            setSelectedIndex,
        }), [
            descendants,
            getDescendants,
            getVisibleDescendants,
            registerDescendant,
            activeIndex,
            setActiveIndex,
            getActiveIndex,
            selected,
            getSelected,
            setSelected,
            setSelectedIndex,
        ]) }, children));
}
export function useDescendant(descendant, context, indexProp, omit) {
    const forceUpdate = useForceUpdate();
    const registerDescendant = useContextSelector(context, ({ registerDescendant }) => registerDescendant);
    const index = useContextSelector(context, ({ descendants }) => indexProp !== null && indexProp !== void 0 ? indexProp : (omit
        ? -1
        : descendants.findIndex((current) => descendant.element === current.element)));
    useLayoutEffect(() => {
        if (!descendant.element) {
            forceUpdate();
        }
        if (!omit) {
            return registerDescendant({
                ...descendant,
                index,
            });
        }
        return;
    }, [
        omit,
        descendant,
        forceUpdate,
        index,
        registerDescendant,
        // eslint-disable-next-line react-hooks/exhaustive-deps
        ...Object.values(descendant),
    ]);
    return index;
}
export function useDescendantsContext(context) {
    return useContext(context);
}
export function useDescendantsContextSelector(context, selector) {
    return useContextSelector(context, selector);
}
export function createDescendantsStepper(options = {}) {
    const { rotate } = options;
    return (getVisibleDescendants, getActiveIndex) => {
        const getFirstDescendant = () => getVisibleDescendants()[0];
        const getLastDescendant = () => {
            const visibleDescendants = getVisibleDescendants();
            return visibleDescendants[visibleDescendants.length - 1];
        };
        const getSelectableIndex = () => getVisibleDescendants().findIndex((descendant) => descendant.index === getActiveIndex());
        const getSafeIndex = (nextIndex) => {
            const visibleDescendants = getVisibleDescendants();
            return ((nextIndex + visibleDescendants.length) % visibleDescendants.length);
        };
        const getRealIndex = (index) => { var _a; return (_a = getVisibleDescendants()[index]) === null || _a === void 0 ? void 0 : _a.index; };
        return {
            getFirstDescendant,
            getLastDescendant,
            getNextDescendant() {
                var _a;
                const selectableIndex = getSelectableIndex();
                const visibleDescendants = getVisibleDescendants();
                if (getRealIndex(selectableIndex) === ((_a = getLastDescendant()) === null || _a === void 0 ? void 0 : _a.index)) {
                    return rotate
                        ? getFirstDescendant()
                        : visibleDescendants[selectableIndex];
                }
                return visibleDescendants[getSafeIndex(selectableIndex + 1)];
            },
            getPreviousDescendant() {
                var _a;
                const selectableIndex = getSelectableIndex();
                const visibleDescendants = getVisibleDescendants();
                if (getRealIndex(selectableIndex) === ((_a = getFirstDescendant()) === null || _a === void 0 ? void 0 : _a.index)) {
                    return rotate
                        ? getLastDescendant()
                        : visibleDescendants[selectableIndex];
                }
                return visibleDescendants[getSafeIndex(selectableIndex - 1)];
            },
            getActiveDescendant() {
                const selectableIndex = getSelectableIndex();
                return getVisibleDescendants()[selectableIndex];
            },
            getMatchedDescendant(filter) {
                return getVisibleDescendants().find(filter);
            },
        };
    };
}
export function useDescendantsStepper(context, stepper) {
    const getActiveIndex = useContextSelector(context, ({ getActiveIndex }) => getActiveIndex);
    const getVisibleDescendants = useContextSelector(context, ({ getVisibleDescendants }) => getVisibleDescendants);
    return useMemo(() => stepper(getVisibleDescendants, getActiveIndex), [getVisibleDescendants, stepper, getActiveIndex]);
}
function findIndexByElement(descendants, element) {
    if (!element || !descendants.length)
        return -1;
    let index = descendants.length;
    while (index--) {
        const currentElement = descendants[index].element;
        if (!currentElement)
            continue;
        if (isElementPreceding(currentElement, element)) {
            return index + 1;
        }
    }
    return -1;
}
function isElementPreceding(a, b) {
    return Boolean(b.compareDocumentPosition(a) & Node.DOCUMENT_POSITION_PRECEDING);
}
function insertAt(array, item, index) {
    if (index == null || !(index in array)) {
        return [...array, item];
    }
    if (index === 0) {
        return [item, ...array];
    }
    return [...array.slice(0, index), item, ...array.slice(index)];
}
//# sourceMappingURL=Descendants.js.map